// Navegador de período COMPARTIDO (Lote 1 de coherencia, 2026-09-07 — antes
// vivía en features/directivo/components; promovido para que coordinador y
// directivo usen exactamente la misma semántica de períodos).
//
// Reemplaza el "filtro" display-only por un control real: ◀ etiqueta ▶ + "Actual".
// El backend ancla en cualquier fecha del período (date_trunc week/month), así que
// alcanza con pasar el `desde` que devuelve la respuesta, desplazado ±7 días (semana)
// o ±1 mes. Todo el cálculo de fechas es en UTC, coherente con el resto de la UI.

import { inicioSemanaOperativa } from 'schemas';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/icon';

function parseUTC(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!));
}

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Desplaza una fecha ISO ±N días (para semanas). */
export function shiftDias(iso: string, dias: number): string {
  const d = parseUTC(iso);
  d.setUTCDate(d.getUTCDate() + dias);
  return toIsoDate(d);
}

/** Desplaza ±N meses y normaliza al día 1 (para meses). */
export function shiftMeses(iso: string, meses: number): string {
  const d = parseUTC(iso);
  d.setUTCMonth(d.getUTCMonth() + meses, 1);
  return toIsoDate(d);
}

/** Primer día de la semana operativa actual (según la fecha local del usuario).
 *  Con default (1) equivale al lunes ISO de la semana actual. */
export function inicioSemanaActual(diaInicioSemana = 1, hoy?: string): string {
  const now = new Date();
  const base =
    hoy ?? toIsoDate(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
  return inicioSemanaOperativa(base, diaInicioSemana);
}

/** Primer día del mes actual (según la fecha local del usuario). */
export function primerDiaMesActual(): string {
  const now = new Date();
  return toIsoDate(new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1)));
}

export function PeriodoNav({
  label,
  isCurrent,
  canNext,
  onPrev,
  onNext,
  onReset,
}: {
  label: string;
  /** true si estamos parados en el período actual (deshabilita "siguiente" y oculta "Actual"). */
  isCurrent: boolean;
  /** false para bloquear ir al futuro. */
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onReset: () => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={onPrev}
        aria-label="Período anterior"
        className="inline-flex size-8 items-center justify-center rounded border border-border text-fg-secondary transition-colors hover:bg-surface-overlay hover:text-fg"
      >
        <Icon name="chevron-left" size={16} stroke={2} />
      </button>
      <span className="inline-flex min-w-[168px] items-center justify-center gap-1.5 text-xs font-medium text-fg">
        <Icon name="calendar" size={13} stroke={2} className="text-fg-tertiary" />
        {label}
      </span>
      <button
        type="button"
        onClick={onNext}
        disabled={!canNext}
        aria-label="Período siguiente"
        className="inline-flex size-8 items-center justify-center rounded border border-border text-fg-secondary transition-colors hover:bg-surface-overlay hover:text-fg disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-fg-secondary"
      >
        <Icon name="chevron-right" size={16} stroke={2} />
      </button>
      {!isCurrent ? (
        <Button variant="ghost" size="sm" onClick={onReset} className="ml-1">
          Actual
        </Button>
      ) : null}
    </div>
  );
}
